import { useMemo, useEffect, useState } from "react";
import type { RoomState } from "ghost-server";

type StateOverride = { state: string; transitionAfterSeconds: number };

type RoomLike = {
	ghostedAt?: Date | string | null;
	lastMessageAt?: Date | string | null;
	createdAt: Date | string;
	stateOverrides?: StateOverride[];
};

// STATE_INFO is now legacy; use color from RoomState

export function useRoomState(room: RoomLike | undefined, states: RoomState[]) {
	const [now, setNow] = useState(() => Date.now());

	useEffect(() => {
		const id = setInterval(
			() =>
				setNow((prev) => {
					const next = Date.now();
					return Math.floor(next / 1000) !== Math.floor(prev / 1000) ? next : prev;
				}),
			1000,
		);
		return () => clearInterval(id);
	}, []);

	return useMemo(() => {
		if (!room || states.length === 0) return null;

		const overrideMap = new Map((room.stateOverrides ?? []).map((o) => [o.state, o.transitionAfterSeconds]));
		const effectiveStates = states
			.map((s) => ({
				state: s.state,
				transitionAfterSeconds: overrideMap.has(s.state) ? overrideMap.get(s.state)! : s.transitionAfterSeconds,
			}))
			.sort((a, b) => a.transitionAfterSeconds - b.transitionAfterSeconds);

		const lastActivity = room.lastMessageAt ?? room.createdAt;
		const elapsed = (now - new Date(lastActivity).getTime()) / 1000;
		const elapsedSeconds = Math.max(0, Math.floor(elapsed));
		const activeElapsed = (now - new Date(room.createdAt).getTime()) / 1000;
		const activeElapsedSeconds = Math.max(0, Math.floor(activeElapsed));

		let current = effectiveStates[0] ?? null;
		let timeUntilNext: number | null = null;

		for (const state of effectiveStates) {
			if (elapsed >= state.transitionAfterSeconds) {
				current = state;
			} else {
				timeUntilNext = Math.ceil(state.transitionAfterSeconds - elapsed);
				break;
			}
		}

		return { current, timeUntilNext, elapsedSeconds, activeElapsedSeconds };
	}, [room, states, now]);
}
